import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';

import Arrow from '@/assets/svg/arrow-left-40px.svg';
import TrashIcon from '@/assets/svg/trash-v2.svg';
import { ScrollStyles } from '@/components/styles';
import { colors } from '@/constants/theme';

interface Props {
  title?: string;
  isOpen: boolean;
  zIndex?: number;
  children?: React.ReactNode;
  isOutsideButton?: boolean;
  isInnerSidePanel?: boolean;
  onDelete?: () => void;
  onClose?: () => void;
}

function SidePanel({
  title,
  isOpen,
  zIndex,
  onClose,
  children,
  isOutsideButton,
  isInnerSidePanel,
  onDelete,
}: Props) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }

    if (!isInnerSidePanel) {
      return () => {
        document.body.style.overflow = 'auto';
      };
    }
  }, [isOpen]);

  return (
    <Component isOpen={isOpen} zIndex={zIndex}>
      <Inner>
        <ButtonsWrapper zIndex={zIndex}>
          <CloseButton onClick={onClose}>
            <ButtonSvg>
              <Arrow />
            </ButtonSvg>
            <ButtonLabel>Назад</ButtonLabel>
          </CloseButton>
          {onDelete && (
            <ClearButton onClick={() => onDelete()}>
              <ClearButtonLabel>Очистить</ClearButtonLabel>
              <ClearButtonSvg>
                <TrashIcon />
              </ClearButtonSvg>
            </ClearButton>
          )}
        </ButtonsWrapper>

        <Content>
          {title && !isOutsideButton && <Title>{title}</Title>}
          {children}
        </Content>
      </Inner>
    </Component>
  );
}

export default SidePanel;

const Component = styled.div<{ isOpen: boolean; zIndex?: number }>`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${colors.white};
  transform: translateX(-100%);
  transition: all 0.3s ease-in-out;
  height: calc(calc(var(--vh, 1vh) * 100));
  ${ScrollStyles};

  ${({ isOpen }) =>
    isOpen &&
    css`
      transform: translateX(0);
    `}

  ${({ zIndex }) =>
    zIndex
      ? css`
          z-index: ${zIndex};
        `
      : css``}
`;

const Inner = styled.div`
  position: relative;
`;

const ButtonsWrapper = styled.div<{
  zIndex?: number;
}>`
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${(props) => props.theme.gray};
  background: ${(props) => props.theme.white_1};

  ${({ zIndex }) =>
    zIndex
      ? css`
          z-index: ${zIndex};
        `
      : css``}
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  height: 60px;
  padding: 20px 10px 15px;
`;

const ButtonLabel = styled.div`
  font-size: 16px;
  line-height: 19px;
  color: ${(props) => props.theme.blue100};
`;

const ButtonSvg = styled.div`
  padding: 2px 10px 0 0;

  svg {
    width: 13.33px;
    height: 13.33px;
    fill: ${(props) => props.theme.blue100};
  }
`;

const Content = styled.div`
  padding: 15px 10px;
  height: calc(var(--vh, 1vh) * 100 - 60px);
  overflow-y: auto;
  background: ${(props) => props.theme.white_1};

  ${ScrollStyles}
`;

const Title = styled.p`
  font-weight: 700;
  font-size: 20px;
  line-height: 23px;
  margin-bottom: 15px;
  color: ${(props) => props.theme.black200};
`;

const ClearButton = styled.button`
  display: flex;
  align-items: center;
  height: 60px;
  padding: 20px 10px 15px;
`;

const ClearButtonLabel = styled.div`
  font-size: 16px;
  line-height: 19px;
  color: ${(props) => props.theme.main};
`;

const ClearButtonSvg = styled.div`
  margin: 4px 0 0 10px;

  svg {
    width: 15px;
    height: 18px;

    path {
      fill: ${(props) => props.theme.main};
    }
  }
`;
